import { DECADES, GENRES, type Decade, type Genre, type SpentStep } from "../../shared/domain.ts";
import { ANY_FILTER, filterKey, type Band, type Filter } from "./pools.ts";

/**
 * Everything the game remembers about a player, on their device.
 *
 * Local-first by design (ADR-adjacent: see docs/future-work.md). Two details
 * exist purely for the account feature that isn't built yet, and both cost
 * almost nothing today:
 *
 *  - `playerId` is minted on the first visit so a future account can *adopt* an
 *    existing history. Signing up must never cost someone their Streak.
 *  - `schemaVersion` is checked on every read so the shape can change later
 *    without wiping anyone.
 */

export const SCHEMA_VERSION = 1;
export const STORAGE_KEY = "spotiguessr:v1";

export interface SlotProgress {
  trackId: number;
  spent: SpentStep[];
  status: "playing" | "won" | "revealed";
}

export interface DailyProgress {
  /** Local date key. A Set in progress is locked to this, so the clock can't re-roll it. */
  date: string;
  slots: SlotProgress[];
  completed: boolean;
}

export interface DailyResult {
  date: string;
  score: number;
  verdicts: SpentStep["verdict"][][];
}

/** How many finished Rounds the Unlimited history keeps. */
export const RECENT_LIMIT = 10;

/**
 * One finished Unlimited Round, kept for the history panel.
 *
 * The artist, title and artwork are denormalised rather than looked up from the
 * Catalog by id: a Catalog rebuild can drop a Track, and a history that developed
 * holes after a rebuild would be worse than one that is slightly stale.
 */
export interface UnlimitedPlay {
  trackId: number;
  artist: string;
  title: string;
  artworkUrl: string | null;
  /** In order, so the panel can draw the same squares as a Share Card. */
  verdicts: SpentStep["verdict"][];
  status: "won" | "revealed";
}

export interface UnlimitedState {
  /** The Mix in play, `"custom"`, or null when the player has never chosen. */
  mixId: string | null;
  /** The player's own Filter, remembered even while a Mix is in play. */
  custom: Filter;
  /**
   * Rounds solved in a row. Deliberately global: it survives changing Mix, so
   * the player is rewarded for playing rather than for staying put.
   */
  streak: number;
  /** Seen Track ids per Filter key, so Unlimited stays fresh across sessions. */
  seen: Record<string, number[]>;
  /** The last RECENT_LIMIT finished Rounds, newest first. */
  recent: UnlimitedPlay[];
}

/**
 * Player preferences.
 *
 * Deliberately short. Every entry here is something the game genuinely cannot
 * decide for the player: how loud a Clip should be, whether the animations are
 * welcome, and — because everything is stored on their own device — a way out.
 */
export interface Settings {
  /** 0–1, applied as the audio graph's gain. */
  volume: number;
  /**
   * Forces motion off regardless of the OS setting.
   *
   * The OS preference is already honoured in CSS; this exists because plenty of
   * people who want a calm page have never found that setting.
   */
  reduceMotion: boolean;
}

export function defaultSettings(): Settings {
  return { volume: 1, reduceMotion: false };
}

export interface StoredState {
  schemaVersion: number;
  playerId: string;
  /** The Daily Streak. Unlimited keeps its own, in `unlimited.streak`. */
  streak: number;
  lastCompletedDate: string | null;
  history: DailyResult[];
  current: DailyProgress | null;
  unlimited: UnlimitedState;
  settings: Settings;
}

export function defaultUnlimited(): UnlimitedState {
  return { mixId: null, custom: ANY_FILTER, streak: 0, seen: {}, recent: [] };
}

export function createState(playerId: string): StoredState {
  return {
    schemaVersion: SCHEMA_VERSION,
    playerId,
    streak: 0,
    lastCompletedDate: null,
    history: [],
    current: null,
    unlimited: defaultUnlimited(),
    settings: defaultSettings(),
  };
}

/**
 * Rebuilds the pre-Mixes seen-sets under the current Filter keys.
 *
 * The old keys were `pop:any` / `any:2010s` — one genre, one decade. Dropping
 * them would silently zero every player's "heard N of M", which is exactly the
 * number the Mix cards exist to show, so the mapping is worth the few lines.
 */
export function migrateSeenKeys(legacy: Record<string, number[]>): Record<string, number[]> {
  const migrated: Record<string, number[]> = {};

  for (const [key, ids] of Object.entries(legacy)) {
    // A current-format key would split wrongly; it can only appear here if
    // something else has already written one, in which case leave it alone.
    if (key.includes("|")) {
      migrated[key] = ids;
      continue;
    }

    const [genre, decade] = key.split(":");
    const filter: Filter = {
      genres: GENRES.includes(genre as Genre) ? [genre as Genre] : [],
      decades: DECADES.includes(decade as Decade) ? [decade as Decade] : [],
      band: null,
    };

    const next = filterKey(filter);
    migrated[next] = [...new Set([...(migrated[next] ?? []), ...ids])];
  }

  return migrated;
}

/**
 * The Unlimited Streak after a Round settles.
 *
 * Solving extends it; walking to the Reveal ends it. A Round still in play
 * leaves it alone, so this is safe to call on every status change.
 */
export function resolveUnlimitedStreak(streak: number, status: SlotProgress["status"]): number {
  if (status === "won") return streak + 1;
  if (status === "revealed") return 0;
  return streak;
}

function readFilter(raw: unknown): Filter {
  const value = (raw ?? {}) as Partial<Filter>;
  const genres = Array.isArray(value.genres) ? value.genres.filter((g) => GENRES.includes(g)) : [];
  const decades = Array.isArray(value.decades)
    ? value.decades.filter((d) => DECADES.includes(d))
    : [];
  const band: Band | null = value.band === "chart" || value.band === "deep" ? value.band : null;
  return { genres, decades, band };
}

function readUnlimited(raw: unknown, legacySeen: Record<string, number[]> | undefined): UnlimitedState {
  if (!raw || typeof raw !== "object") {
    return { ...defaultUnlimited(), seen: migrateSeenKeys(legacySeen ?? {}) };
  }

  const value = raw as Partial<UnlimitedState>;
  return {
    mixId: typeof value.mixId === "string" ? value.mixId : null,
    custom: readFilter(value.custom),
    streak: typeof value.streak === "number" ? value.streak : 0,
    seen: value.seen ?? {},
    // Defaulted rather than version-bumped: `parseState` discards the whole
    // state on a schemaVersion mismatch, so adding a field via a bump would cost
    // every existing player their Streak. A new field that reads as empty is the
    // migration.
    recent: readRecent(value.recent),
  };
}

function readRecent(raw: unknown): UnlimitedPlay[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry): entry is UnlimitedPlay => {
      if (!entry || typeof entry !== "object") return false;
      const play = entry as Partial<UnlimitedPlay>;
      return (
        typeof play.trackId === "number" &&
        typeof play.artist === "string" &&
        typeof play.title === "string" &&
        Array.isArray(play.verdicts) &&
        (play.status === "won" || play.status === "revealed")
      );
    })
    .slice(0, RECENT_LIMIT);
}

/**
 * Records a finished Unlimited Round, newest first.
 *
 * De-duplicated by Track id so replaying the same song after a Pool reset does
 * not show it twice — the newer attempt wins, which is what the player just did.
 */
export function recordUnlimitedPlay(state: StoredState, play: UnlimitedPlay): StoredState {
  const recent = [play, ...state.unlimited.recent.filter((p) => p.trackId !== play.trackId)].slice(
    0,
    RECENT_LIMIT,
  );
  return { ...state, unlimited: { ...state.unlimited, recent } };
}

function readSettings(raw: unknown): Settings {
  const value = (raw ?? {}) as Partial<Settings>;
  const volume = typeof value.volume === "number" && Number.isFinite(value.volume) ? value.volume : 1;
  return {
    // Clamped rather than trusted: a stored 40 would be deafening and a stored
    // -1 would invert the waveform.
    volume: Math.min(1, Math.max(0, volume)),
    reduceMotion: value.reduceMotion === true,
  };
}

function newPlayerId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `p-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Reads stored state, discarding anything it doesn't understand.
 *
 * An unknown schemaVersion means a *newer* build wrote this — clobbering it
 * with a downgrade would be worse than starting fresh.
 */
export function parseState(raw: string | null): StoredState | null {
  if (!raw) return null;

  try {
    // `unlimitedSeen` is where Unlimited's seen-sets lived before Mixes. It is
    // read here and nowhere else, so a returning player keeps their progress.
    const parsed = JSON.parse(raw) as Partial<StoredState> & {
      unlimitedSeen?: Record<string, number[]>;
    };
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (typeof parsed.playerId !== "string") return null;

    return {
      schemaVersion: SCHEMA_VERSION,
      playerId: parsed.playerId,
      streak: typeof parsed.streak === "number" ? parsed.streak : 0,
      lastCompletedDate: parsed.lastCompletedDate ?? null,
      history: Array.isArray(parsed.history) ? parsed.history : [],
      current: parsed.current ?? null,
      unlimited: readUnlimited(parsed.unlimited, parsed.unlimitedSeen),
      // Defaulted rather than version-bumped, like `unlimited.recent`: a bump
      // discards the whole state and would cost every player their Streak.
      settings: readSettings(parsed.settings),
    };
  } catch {
    return null;
  }
}

export function loadState(storage: Pick<Storage, "getItem"> | null): StoredState {
  const existing = storage ? parseState(storage.getItem(STORAGE_KEY)) : null;
  return existing ?? createState(newPlayerId());
}

export function saveState(storage: Pick<Storage, "setItem"> | null, state: StoredState): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing and full quotas both throw. Losing a Streak is bad;
    // crashing the game mid-Round is worse.
  }
}

/** The calendar day before `dateKey`, in the player's own timezone. */
export function previousDateKey(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y!, m! - 1, d!);
  date.setDate(date.getDate() - 1);
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${mm}-${dd}`;
}

/** Score is total Steps across the Set — five is perfect, thirty is a whiff. */
export function scoreOf(slots: SlotProgress[]): number {
  return slots.reduce((total, slot) => total + slot.spent.length, 0);
}

/**
 * Records a completed Daily Set.
 *
 * The Streak counts *showing up*, never how well you did — a bad Score costs
 * points and nothing else. Missing a day resets it; there are no freezes.
 */
export function completeDaily(
  state: StoredState,
  progress: DailyProgress,
): StoredState {
  const { date, slots } = progress;

  // Finishing the same day twice must not inflate the Streak.
  if (state.lastCompletedDate === date) return state;

  const continues = state.lastCompletedDate === previousDateKey(date);

  return {
    ...state,
    streak: continues ? state.streak + 1 : 1,
    lastCompletedDate: date,
    history: [
      ...state.history.filter((h) => h.date !== date),
      { date, score: scoreOf(slots), verdicts: slots.map((s) => s.spent.map((x) => x.verdict)) },
    ].slice(-90),
    current: { ...progress, completed: true },
  };
}

/**
 * The Streak as it stands *today*, rather than the last value written.
 *
 * `state.streak` only changes when a Set is completed, so after a missed day the
 * stored number is stale and the UI would keep showing a Streak the player has
 * already lost. A Streak is alive only if the last completed Set was today or
 * yesterday.
 */
export function currentStreak(state: StoredState, today: string): number {
  if (!state.lastCompletedDate) return 0;
  if (state.lastCompletedDate === today) return state.streak;
  if (state.lastCompletedDate === previousDateKey(today)) return state.streak;
  return 0;
}

/** True when the stored Set belongs to a day that has already rolled over. */
export function isStale(progress: DailyProgress | null, today: string): boolean {
  return progress !== null && progress.date !== today;
}
